import React, {useEffect} from 'react';
import ActionButton from '../../components/ActionButton';
import bellIcon from '../emailConfirmation-0424/assets/bellIcon.svg';
import logo from '../emailConfirmation-0424/assets/logo@3x.png';
import openOutsideIcon from '../emailConfirmation-0424/assets/openOutsideIcon.svg';
import '../emailConfirmation-0424/screens/EmailConfirmation.css';
import '../expiredPaywall/ExpiredPaywallSuccessScreenTimeline.css';
import {useQueryToken} from '../../utils/dynamicCodes/hooks';
import Analytics from '../../utils/Analytics';
import Environment from '../../utils/Environment';
import {Trans, useTranslation} from 'react-i18next';
import validateEmail from '../../utils/validateEmail';
import {Helmet} from 'react-helmet';
import useDocumentTitle from '../../hooks/useDocumentTitle';

function TrialCancelSuccess() {
  const {t} = useTranslation();
  useDocumentTitle('Rise');
  const emailAddress = useQueryToken();

  const handleContinue = () => {
    Analytics.track('Trial Cancel Paywall Success Continue Clicked', {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: emailAddress,
    });
    window.location.href = 'nyx://';
  };

  useEffect(() => {
    Analytics.track('Trial Cancel Paywall Success Screen Viewed', {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: emailAddress,
    });
  }, [emailAddress]);

  useEffect(() => {
    Analytics.track('Auto Redirect to App from Trial Cancel Paywall Success', {
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: emailAddress,
    });
    window.location.href = 'nyx://';
  }, [emailAddress]);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      <div className="safe-full-screen w-screen overflow-hidden flex flex-col justify-between text-white">
        <div className="">
          <div className="flex flex-row items-center pt-10 pb-8 px-2">
            <img src={logo} alt={t('{{brand}}')} width={34} height={34} />
            <div className="ml-2">
              <div className="text-xl font-bold leading-none">{t('{{brand}}')}</div>
              <div className="text-xl font-bold text-yellow-2 leading-none">Plan Updated!</div>
            </div>
          </div>
          <div className="flex flex-col gap-0 mr-10 ml-2 text-xs leading-tight">
            {/* Step 1 */}
            <div className="flex flex-row items-stretch">
              {/* Timeline: ball + line */}
              <div className="flex flex-col items-center relative" style={{minWidth: 24}}>
                <div className="w-4 h-4 rounded-full bg-purple-2 border-2 border-purple-2 mt-2 z-10" />
                <div className="timeline-segment-1" />
              </div>
              {/* Content */}
              <div className="flex-1 pl-4 pb-6" style={{marginTop: 3}}>
                <h2 className="text-lg">{t('expired.success_step_1_title')}</h2>
                <div>
                  Your subscription to Rise is updated. Click below to open the app and continue optimizing your sleep &
                  energy.
                </div>
                <div className="pt-2">
                  <ActionButton icon={openOutsideIcon} title={t('expired.resume_rise')} onClick={handleContinue} />
                </div>
              </div>
            </div>
            {/* Step 2 */}
            <div className="flex flex-row items-stretch">
              <div className="flex flex-col items-center relative" style={{minWidth: 24}}>
                <div
                  className="w-4 h-4 rounded-full border-2 z-10"
                  style={{backgroundColor: '#A259E6', borderColor: '#A259E6'}}
                />
                <div className="timeline-segment-2" />
              </div>
              <div className="flex-1 pl-4 pb-6" style={{marginTop: -3}}>
                <h2 className="text-lg">{t('expired.success_step_2_title')}</h2>
                <div>{t('expired.success_step_2_subtitle')}</div>
              </div>
            </div>
            {/* Step 3 */}
            <div className="flex flex-row items-stretch">
              <div className="flex flex-col items-center relative" style={{minWidth: 24}}>
                <div
                  className="w-4 h-4 rounded-full bg-purple-2 border-2 border-purple-2 z-10"
                  style={{backgroundColor: '#D28B73', borderColor: '#D28B73'}}
                />
                <div className="timeline-segment-3" />
              </div>
              <div className="flex-1 pl-4 pb-6" style={{marginTop: -3}}>
                <h2 className="text-lg">
                  {t('expired.success_step_3_title')}
                  <img src={bellIcon} alt="bell" className="w-5 h-5 ml-2 inline" />
                </h2>
                {emailAddress && validateEmail(emailAddress) ? (
                  <Trans
                    components={{highlight: <span className="text-yellow-2" />}}
                    i18nKey="expired.success_step_3_subtitle"
                    values={{emailAddress}}
                  />
                ) : (
                  <p>{t('expired.success_step_3_subtitle_fallback')}</p>
                )}
              </div>
            </div>
            {/* Step 4 */}
            <div className="flex flex-row items-stretch">
              <div className="flex flex-col items-center relative" style={{minWidth: 24}}>
                <div
                  className="w-4 h-4 rounded-full bg-purple-2 border-2 border-purple-2 z-10"
                  style={{backgroundColor: '#FFEB3B', borderColor: '#FFEB3B'}}
                />
                {/* No line for last step */}
              </div>
              <div className="flex-1 pl-4 pb-2" style={{marginTop: -3}}>
                <h2 className="text-xl">{t('expired.success_step_4_title')}</h2>
                <div>{t('expired.success_step_4_subtitle')}</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="p-2">
            <ActionButton icon={openOutsideIcon} title={t('expired.resume_rise')} onClick={handleContinue} />

            <div className="my-2 text-center text-sm text-purple-2 font-bold">
              <a href="mailto:support@risescience.com">{t('common.Contact_us')}</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TrialCancelSuccess;
